import React from 'react';
import Card from '../../components/ui/Card.jsx';
import { apiFetch } from '../../api.js';

const ENROLLMENT_COLORS = ['#0E7490', '#A16207', '#2563EB', '#166534', '#B45309'];
const COURSE_COLORS = ['#0E7490', '#A16207', '#1D4ED8', '#15803D', '#B91C1C'];

export default function StaffAnalytics() {
  const [enrollmentFilters, setEnrollmentFilters] = React.useState({
    semester: '',
    year: '',
    scope: 'all'
  });
  const [enrollmentState, setEnrollmentState] = React.useState({
    loading: true,
    data: null,
    error: ''
  });

  const [courseFilters, setCourseFilters] = React.useState({
    semester: '',
    year: '',
    scope: 'all'
  });
  const [courseInputs, setCourseInputs] = React.useState(['']);
  const [courseState, setCourseState] = React.useState({
    loading: false,
    data: null,
    error: ''
  });

  const normalizedCourses = React.useMemo(
    () => dedupeCourseInputs(courseInputs),
    [courseInputs]
  );
  const selectedCourses = normalizedCourses.items;
  const selectedCoursesKey = selectedCourses.join('|');

  React.useEffect(() => {
    const termError = validateSemesterYear(enrollmentFilters.semester, enrollmentFilters.year);
    if (termError) {
      setEnrollmentState({ loading: false, data: null, error: termError });
      return;
    }

    let active = true;
    setEnrollmentState((prev) => ({ ...prev, loading: true, error: '' }));

    const params = buildAnalyticsParams(enrollmentFilters);
    apiFetch(`/api/analytics/enrollment?${params.toString()}`)
      .then((data) => {
        if (!active) return;
        setEnrollmentState({ loading: false, data, error: '' });
      })
      .catch((err) => {
        if (!active) return;
        setEnrollmentState({
          loading: false,
          data: null,
          error: err?.message || 'Failed to load enrollment analytics.'
        });
      });

    return () => {
      active = false;
    };
  }, [enrollmentFilters.semester, enrollmentFilters.year, enrollmentFilters.scope]);

  React.useEffect(() => {
    const termError = validateSemesterYear(courseFilters.semester, courseFilters.year);
    if (termError) {
      setCourseState({ loading: false, data: null, error: termError });
      return;
    }

    if (selectedCourses.length === 0) {
      setCourseState({
        loading: false,
        data: null,
        error: 'Enter at least one course to compare.'
      });
      return;
    }

    let active = true;
    setCourseState((prev) => ({ ...prev, loading: true, error: '' }));

    const params = buildAnalyticsParams(courseFilters, selectedCourses);
    apiFetch(`/api/analytics/courses?${params.toString()}`)
      .then((data) => {
        if (!active) return;
        setCourseState({ loading: false, data, error: '' });
      })
      .catch((err) => {
        if (!active) return;
        setCourseState({
          loading: false,
          data: null,
          error: err?.message || 'Failed to load course analytics.'
        });
      });

    return () => {
      active = false;
    };
  }, [courseFilters.semester, courseFilters.year, courseFilters.scope, selectedCoursesKey]);

  const enrollmentItems = enrollmentState.data?.items || [];
  const enrollmentYears = enrollmentState.data?.available_filters?.years || [];
  const enrollmentTotals = enrollmentState.data?.totals || {};

  const courseItems = courseState.data?.items || [];
  const courseYears = courseState.data?.available_filters?.years || [];
  const courseSuggestions = courseState.data?.course_suggestions || [];
  const courseCohortCount = courseState.data?.cohort?.students_in_filtered_cohort || 0;

  return (
    <div className="page analytics-page">
      <div className="page-header">
        <div>
          <h1>Analytics</h1>
          <p className="subtle">Track enrollment cohorts from start semester/year and compare study-plan course demand.</p>
        </div>
      </div>

      <Card title="Student Enrollment">
        <div className="analytics-stack">
          <p className="subtle analytics-note">Data source: <code>student_info.start_semester</code> + <code>student_info.start_year</code>.</p>
          <div className="analytics-controls">
            <label className="input-field">
              <span className="input-label">Semester</span>
              <select
                className="input"
                value={enrollmentFilters.semester}
                onChange={(event) => updateFilter(setEnrollmentFilters, 'semester', event.target.value)}
              >
                <option value="">All semesters</option>
                <option value="Spring">Spring</option>
                <option value="Fall">Fall</option>
              </select>
            </label>

            <label className="input-field">
              <span className="input-label">Year</span>
              <input
                className="input"
                type="number"
                min="1900"
                max="2099"
                placeholder="All years"
                value={enrollmentFilters.year}
                list="enrollment-year-list"
                onChange={(event) => updateFilter(setEnrollmentFilters, 'year', event.target.value)}
              />
              <datalist id="enrollment-year-list">
                {enrollmentYears.map((year) => (
                  <option key={year} value={year} />
                ))}
              </datalist>
            </label>

            <label className="input-field">
              <span className="input-label">Student scope</span>
              <select
                className="input"
                value={enrollmentFilters.scope}
                onChange={(event) => updateFilter(setEnrollmentFilters, 'scope', event.target.value)}
              >
                <option value="all">All students</option>
                <option value="active">Active only</option>
              </select>
            </label>
          </div>

          {enrollmentState.error ? <div className="alert error">{enrollmentState.error}</div> : null}
          {enrollmentState.loading ? <p className="subtle">Loading enrollment analytics...</p> : null}

          {!enrollmentState.loading && !enrollmentState.error ? (
            <>
              <div className="analytics-metrics">
                <div className="analytics-metric">
                  <div className="analytics-metric-label">Students Considered</div>
                  <div className="analytics-metric-value">{enrollmentTotals.students_considered || 0}</div>
                </div>
              </div>

              <EnrollmentChart items={enrollmentItems} />

              <div className="analytics-table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th scope="col">Semester</th>
                      <th scope="col">Year</th>
                      <th scope="col">Students</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollmentItems.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="analytics-empty">No enrollment records found.</td>
                      </tr>
                    ) : (
                      enrollmentItems.map((item) => (
                        <tr key={item.label}>
                          <td>{item.semester}</td>
                          <td>{item.year}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      </Card>

      <Card title="Study-Plan Course Comparison">
        <div className="analytics-stack">
          <div className="analytics-controls">
            <label className="input-field">
              <span className="input-label">Semester</span>
              <select
                className="input"
                value={courseFilters.semester}
                onChange={(event) => updateFilter(setCourseFilters, 'semester', event.target.value)}
              >
                <option value="">All semesters</option>
                <option value="Spring">Spring</option>
                <option value="Fall">Fall</option>
              </select>
            </label>

            <label className="input-field">
              <span className="input-label">Year</span>
              <input
                className="input"
                type="number"
                min="1900"
                max="2099"
                placeholder="All years"
                value={courseFilters.year}
                list="course-year-list"
                onChange={(event) => updateFilter(setCourseFilters, 'year', event.target.value)}
              />
              <datalist id="course-year-list">
                {courseYears.map((year) => (
                  <option key={year} value={year} />
                ))}
              </datalist>
            </label>

            <label className="input-field">
              <span className="input-label">Student scope</span>
              <select
                className="input"
                value={courseFilters.scope}
                onChange={(event) => updateFilter(setCourseFilters, 'scope', event.target.value)}
              >
                <option value="all">All students</option>
                <option value="active">Active only</option>
              </select>
            </label>
          </div>

          <div className="analytics-course-inputs">
            {courseInputs.map((value, index) => (
              <div className="analytics-course-input-row" key={`course-input-${index}`}>
                <label className="input-field">
                  <span className="input-label">Course {index + 1}</span>
                  <input
                    className="input"
                    list="course-suggestions"
                    value={value}
                    placeholder="EE 4630"
                    onChange={(event) => updateCourseInput(setCourseInputs, index, event.target.value)}
                  />
                </label>
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={() => removeCourseInput(setCourseInputs, index)}
                  disabled={courseInputs.length <= 1}
                  aria-label={`Remove course ${index + 1}`}
                >
                  Remove
                </button>
              </div>
            ))}

            <div className="analytics-course-actions">
              <button
                className="btn btn-secondary"
                type="button"
                disabled={courseInputs.length >= 5}
                onClick={() => setCourseInputs((prev) => [...prev, ''])}
              >
                Add Course
              </button>
              <span className="subtle">Compare up to five courses.</span>
            </div>
            {normalizedCourses.duplicates > 0 ? (
              <p className="subtle analytics-note">
                Duplicate courses are merged automatically.
              </p>
            ) : null}
          </div>

          <datalist id="course-suggestions">
            {courseSuggestions.map((course) => (
              <option key={course} value={course} />
            ))}
          </datalist>

          {courseState.error ? <div className="alert error">{courseState.error}</div> : null}
          {courseState.loading ? <p className="subtle">Loading course comparison...</p> : null}

          {!courseState.loading && !courseState.error && courseItems.length > 0 ? (
            <>
              <div className="analytics-metrics">
                <div className="analytics-metric">
                  <div className="analytics-metric-label">Students in Cohort</div>
                  <div className="analytics-metric-value">{courseCohortCount}</div>
                </div>
                <div className="analytics-metric">
                  <div className="analytics-metric-label">Courses Compared</div>
                  <div className="analytics-metric-value">{courseItems.length}</div>
                </div>
              </div>

              <div className="analytics-course-bars" role="img" aria-label="Comparison of selected course counts">
                {courseItems.map((item, index) => (
                  <CourseBar
                    key={item.normalized_key}
                    item={item}
                    max={Math.max(...courseItems.map((entry) => entry.count), 1)}
                    color={COURSE_COLORS[index % COURSE_COLORS.length]}
                  />
                ))}
              </div>

              <div className="analytics-table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th scope="col">Course</th>
                      <th scope="col">Normalized Key</th>
                      <th scope="col">Students</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseItems.map((item) => (
                      <tr key={`table-${item.normalized_key}`}>
                        <td>{item.course}</td>
                        <td>{item.normalized_key}</td>
                        <td>{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function EnrollmentChart({ items }) {
  if (items.length === 0) {
    return <p className="subtle">No chart data is available for this filter.</p>;
  }

  const chartHeight = 280;
  const margin = { top: 18, right: 16, bottom: 56, left: 40 };
  const innerHeight = chartHeight - margin.top - margin.bottom;
  const innerWidth = Math.max(360, items.length * 84);
  const totalWidth = margin.left + innerWidth + margin.right;
  const maxCount = Math.max(...items.map((item) => item.count), 1);
  const bucketWidth = innerWidth / items.length;
  const barWidth = Math.min(52, Math.max(30, bucketWidth - 20));

  return (
    <div className="analytics-chart-scroll">
      <svg
        className="analytics-svg-chart"
        viewBox={`0 0 ${totalWidth} ${chartHeight}`}
        role="img"
        aria-label="Student enrollment by semester and year"
      >
        {[0, 1, 2, 3, 4].map((step) => {
          const ratio = step / 4;
          const y = chartHeight - margin.bottom - ratio * innerHeight;
          const value = Math.round(maxCount * ratio);
          return (
            <g key={`grid-${step}`}>
              <line
                x1={margin.left}
                y1={y}
                x2={margin.left + innerWidth}
                y2={y}
                stroke="var(--border-subtle)"
                strokeWidth="1"
              />
              <text x={margin.left - 8} y={y + 4} textAnchor="end" className="analytics-axis-label">
                {value}
              </text>
            </g>
          );
        })}

        {items.map((item, index) => {
          const x = margin.left + index * bucketWidth + (bucketWidth - barWidth) / 2;
          const barHeight = (item.count / maxCount) * innerHeight;
          const y = chartHeight - margin.bottom - barHeight;
          const color = ENROLLMENT_COLORS[index % ENROLLMENT_COLORS.length];
          return (
            <g key={item.label}>
              <rect x={x} y={y} width={barWidth} height={barHeight} rx="10" fill={color} />
              <text x={x + barWidth / 2} y={Math.max(y - 8, margin.top + 12)} textAnchor="middle" className="analytics-bar-value">
                {item.count}
              </text>
              <text x={x + barWidth / 2} y={chartHeight - margin.bottom + 18} textAnchor="middle" className="analytics-axis-label">
                {item.semester}
              </text>
              <text x={x + barWidth / 2} y={chartHeight - margin.bottom + 34} textAnchor="middle" className="analytics-axis-label">
                {item.year}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function CourseBar({ item, max, color }) {
  const width = max <= 0 ? 0 : Math.round((item.count / max) * 100);
  return (
    <div className="analytics-course-bar-row">
      <div className="analytics-course-label">{item.course}</div>
      <div className="analytics-course-track" aria-hidden="true">
        <div className="analytics-course-fill" style={{ width: `${width}%`, backgroundColor: color }} />
      </div>
      <div className="analytics-course-count">{item.count}</div>
    </div>
  );
}

function buildAnalyticsParams(filters, courses = []) {
  const params = new URLSearchParams();
  params.set('scope', filters.scope || 'all');

  const semester = String(filters.semester || '').trim();
  const year = String(filters.year || '').trim();
  if (semester && year) {
    params.set('semester', semester);
    params.set('year', year);
  }

  courses.forEach((course) => params.append('courses', course));
  return params;
}

function validateSemesterYear(semester, year) {
  const hasSemester = String(semester || '').trim() !== '';
  const hasYear = String(year || '').trim() !== '';
  if (hasSemester !== hasYear) {
    return 'Semester and year must be selected together.';
  }
  return '';
}

function updateFilter(setter, key, value) {
  setter((prev) => ({ ...prev, [key]: value }));
}

function updateCourseInput(setter, index, value) {
  setter((prev) => prev.map((current, idx) => (idx === index ? value : current)));
}

function removeCourseInput(setter, index) {
  setter((prev) => {
    if (prev.length <= 1) return prev;
    return prev.filter((_, idx) => idx !== index);
  });
}

function dedupeCourseInputs(values) {
  const seen = new Set();
  const items = [];
  let duplicates = 0;

  values.forEach((raw) => {
    const normalized = normalizeCourseKey(raw);
    if (!normalized) return;
    if (seen.has(normalized)) {
      duplicates++;
      return;
    }
    seen.add(normalized);
    items.push(formatCourseDisplay(raw));
  });

  return { items, duplicates };
}

function normalizeCourseKey(raw) {
  return String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/[\s\-_]+/g, '');
}

function formatCourseDisplay(raw) {
  return String(raw || '')
    .trim()
    .toUpperCase()
    .split(/[\s\-_]+/)
    .filter(Boolean)
    .join(' ');
}
